/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type { DesktopViewState, DesktopWindowStatePatch } from '@poweralpha/common';
import * as z from 'zod';
import { listDraftKeys } from './definitions';
import { decodeAppState, encodeAppState, jsonCodec } from './storage';

/** Window-wide state: shared by every view of a window (e.g. each tab's last search). */
export const WINDOW_VIEW = '';

/** Coalesce keystrokes: form values and results (BOMs) are expensive to send. */
const SAVE_DELAY_MS = 1000;

export interface DesktopWindowStateApi {
  load: (windowId: string) => Promise<Record<string, DesktopViewState>>;
  /** Saves a merge patch for one view (`POST`). */
  save: (windowId: string, patch: DesktopWindowStatePatch) => Promise<void>;
  removeWindow: (windowId: string) => Promise<void>;
}

interface WindowEntry {
  status: 'loading' | 'ready';
  views: Map<string, DesktopViewState>;
}

interface PendingPatch {
  windowId: string;
  view: string;
  set: DesktopViewState;
  remove: Set<string>;
}

type Listener = () => void;

const pendingBufferCodec = jsonCodec(
  z.array(
    z.object({
      windowId: z.string(),
      view: z.string(),
      set: z.record(z.string(), z.unknown()),
      remove: z.array(z.string()),
    }),
  ),
);

const getPendingBufferKey = (userId: string) => `pa-desktop-pending:v1:${userId}`;

/**
 * What each desktop window view needs to come back as the user left it (form drafts, unsaved
 * results, window-wide memory), saved on the server per window and view. Kept outside the
 * desktop reducer, so writes never re-render windows.
 *
 * Reads come from a per-window cache loaded once per window; writes update the cache at once and
 * reach the server as debounced merge patches. A patch still pending when the page is hidden
 * waits in local storage and is sent before any window loads next time.
 *
 * Also holds each window's page title (memory only), shown for the focused window.
 */
export class DesktopAppStateStore {
  private readonly windows = new Map<string, WindowEntry>();
  private readonly pending = new Map<string, PendingPatch>();
  /** Sent but not yet confirmed; buffered too when the page hides mid-request. */
  private inFlight: PendingPatch[] = [];
  private readonly titles = new Map<string, string>();
  private readonly listeners = new Set<Listener>();
  /** Readers of one window + view + key, so a write re-renders only them. */
  private readonly keyListeners = new Map<string, Set<Listener>>();
  private readonly titleListeners = new Set<Listener>();
  private readonly started: Promise<void>;
  private resolveStarted: () => void = () => undefined;
  private userId: string | null = null;
  private saveTimer: ReturnType<typeof setTimeout> | undefined;
  private saveFailed = false;

  constructor(private readonly api: DesktopWindowStateApi) {
    this.started = new Promise((resolve) => {
      this.resolveStarted = resolve;
    });
  }

  /**
   * Called once the desktop session (and so the user) is known. Sends patches left over from the
   * last page, then lets windows load.
   */
  async start(userId: string | null) {
    this.userId = userId;
    const buffered = userId ? this.takeBufferedPatches(userId) : [];
    for (const patch of buffered) {
      this.queue(patch.windowId, patch.view, patch.set, patch.remove);
    }
    await this.flush();
    this.resolveStarted();
  }

  /** Loads a window's views once; the window's content mounts when it is ready. */
  async ensureLoaded(windowId: string) {
    if (this.windows.has(windowId)) return;
    const entry: WindowEntry = { status: 'loading', views: new Map() };
    this.windows.set(windowId, entry);
    this.notify();
    await this.started;
    try {
      const views = await this.api.load(windowId);
      const decoded = decodeAppState<Record<string, DesktopViewState>>(JSON.stringify(views));
      for (const [view, state] of Object.entries(decoded)) {
        // Writes made while loading win over what was saved.
        entry.views.set(view, { ...state, ...entry.views.get(view) });
      }
    } catch {
      // Unavailable (e.g. no desktop tables locally): the window starts without saved state.
    }
    entry.status = 'ready';
    this.notify();
    this.notifyWindowKeys(windowId);
  }

  isReady(windowId: string) {
    return this.windows.get(windowId)?.status === 'ready';
  }

  hasDraft(windowId: string) {
    const entry = this.windows.get(windowId);
    // Saved drafts are unknown until the scope loads: assume one, so closing still confirms.
    if (entry?.status === 'loading') return true;
    const keys = listDraftKeys();
    return [...(entry?.views.values() ?? [])].some(
      (view) =>
        keys.some((key) => key in view) ||
        Object.values(view).some(
          (value) =>
            value !== null && typeof value === 'object' && 'v' in value && 'values' in value,
        ),
    );
  }

  hasSaveFailed() {
    return this.saveFailed;
  }

  read<T>(windowId: string, view: string, key: string): T | undefined {
    return this.windows.get(windowId)?.views.get(view)?.[key] as T | undefined;
  }

  write(windowId: string, view: string, key: string, value: unknown) {
    const views = this.getViews(windowId);
    views.set(view, { ...views.get(view), [key]: value });
    this.queue(windowId, view, { [key]: value }, []);
    this.notifyKey(windowId, view, key);
  }

  remove(windowId: string, view: string, key: string) {
    const state = this.windows.get(windowId)?.views.get(view);
    if (state && key in state) {
      const { [key]: _removed, ...rest } = state;
      this.getViews(windowId).set(view, rest);
    }
    this.queue(windowId, view, {}, [key]);
    this.notifyKey(windowId, view, key);
  }

  /** Forgets a closed window on the server and here: views, pending saves and title. */
  removeWindow(windowId: string) {
    this.windows.delete(windowId);
    this.titles.delete(windowId);
    for (const [id, patch] of this.pending) {
      if (patch.windowId === windowId) this.pending.delete(id);
    }
    this.api.removeWindow(windowId).catch(() => undefined);
    this.notify();
    this.notifyWindowKeys(windowId);
  }

  /** Sends pending patches now; failed ones are kept and retried with the next save. */
  async flush() {
    clearTimeout(this.saveTimer);
    this.saveTimer = undefined;
    const patches = [...this.pending.values()];
    if (!patches.length) return;
    this.pending.clear();
    this.inFlight = patches;
    const results = await Promise.allSettled(
      patches.map((patch) => this.api.save(patch.windowId, this.toRequest(patch))),
    );
    this.inFlight = [];
    const failed = patches.filter((_, index) => results[index].status === 'rejected');
    for (const patch of failed) {
      // Changes queued meanwhile are newer: merge the failed patch underneath them.
      const newer = this.pending.get(this.getPendingId(patch.windowId, patch.view));
      this.pending.delete(this.getPendingId(patch.windowId, patch.view));
      this.queue(patch.windowId, patch.view, patch.set, patch.remove, false);
      if (newer) this.queue(newer.windowId, newer.view, newer.set, newer.remove, false);
    }
    if (failed.length && !this.saveTimer) this.scheduleSave();
    // Everything buffered at the last page hide has been saved now: do not replay it later.
    if (!failed.length && this.userId) this.clearBufferedPatches(this.userId);
    this.setSaveFailed(failed.length > 0);
  }

  /** Page hiding: a network save may not finish, so keep pending patches for the next load. */
  bufferPendingPatches() {
    const unsaved = [...this.inFlight, ...this.pending.values()];
    if (!this.userId || !unsaved.length) return;
    const patches = [
      ...this.takeBufferedPatches(this.userId),
      ...unsaved.map((patch) => ({
        windowId: patch.windowId,
        view: patch.view,
        set: patch.set,
        remove: [...patch.remove],
      })),
    ];
    try {
      localStorage.setItem(getPendingBufferKey(this.userId), pendingBufferCodec.encode(patches));
    } catch {
      // Storage unavailable or full: the next network save still has them if the page survives.
    }
  }

  /** Subscribes to one value; for `useSyncExternalStore`. */
  subscribeKey(windowId: string, view: string, key: string, listener: Listener) {
    const id = this.getKeyId(windowId, view, key);
    const listeners = this.keyListeners.get(id) ?? new Set<Listener>();
    listeners.add(listener);
    this.keyListeners.set(id, listeners);
    return () => {
      listeners.delete(listener);
      if (!listeners.size) this.keyListeners.delete(id);
    };
  }

  subscribe(listener: Listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  getTitle(windowId: string) {
    return this.titles.get(windowId);
  }

  setTitle(windowId: string, title: string | undefined) {
    if (this.titles.get(windowId) === title) return;
    if (title === undefined) this.titles.delete(windowId);
    else this.titles.set(windowId, title);
    for (const listener of this.titleListeners) listener();
  }

  subscribeTitles(listener: Listener) {
    this.titleListeners.add(listener);
    return () => {
      this.titleListeners.delete(listener);
    };
  }

  private getViews(windowId: string) {
    let entry = this.windows.get(windowId);
    if (!entry) {
      entry = { status: 'ready', views: new Map() };
      this.windows.set(windowId, entry);
    }
    return entry.views;
  }

  private queue(
    windowId: string,
    view: string,
    set: DesktopViewState,
    remove: Iterable<string>,
    schedule = true,
  ) {
    const id = this.getPendingId(windowId, view);
    const patch = this.pending.get(id) ?? { windowId, view, set: {}, remove: new Set<string>() };
    for (const key of remove) {
      delete patch.set[key];
      patch.remove.add(key);
    }
    for (const [key, value] of Object.entries(set)) {
      patch.set[key] = value;
      patch.remove.delete(key);
    }
    this.pending.set(id, patch);
    if (schedule) this.scheduleSave();
  }

  private scheduleSave() {
    clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => void this.flush(), SAVE_DELAY_MS);
  }

  private toRequest(patch: PendingPatch): DesktopWindowStatePatch {
    return {
      view: patch.view,
      // Tag dates so they load as dates.
      set: JSON.parse(encodeAppState(patch.set)) as DesktopViewState,
      remove: [...patch.remove],
    };
  }

  private takeBufferedPatches(userId: string) {
    try {
      const key = getPendingBufferKey(userId);
      const stored = localStorage.getItem(key);
      localStorage.removeItem(key);
      const decoded = stored === null ? undefined : pendingBufferCodec.safeDecode(stored);
      return decoded?.success ? decoded.data : [];
    } catch {
      return [];
    }
  }

  private clearBufferedPatches(userId: string) {
    try {
      localStorage.removeItem(getPendingBufferKey(userId));
    } catch {
      // Storage unavailable: nothing was buffered.
    }
  }

  private getPendingId(windowId: string, view: string) {
    return `${windowId}\u0000${view}`;
  }

  private setSaveFailed(saveFailed: boolean) {
    if (this.saveFailed === saveFailed) return;
    this.saveFailed = saveFailed;
    this.notify();
  }

  private getKeyId(windowId: string, view: string, key: string) {
    return `${windowId}\u0000${view}\u0000${key}`;
  }

  private notifyKey(windowId: string, view: string, key: string) {
    for (const listener of this.keyListeners.get(this.getKeyId(windowId, view, key)) ?? []) {
      listener();
    }
  }

  /** Loading or removing a window changes every value in it. */
  private notifyWindowKeys(windowId: string) {
    const prefix = `${windowId}\u0000`;
    for (const [id, listeners] of this.keyListeners) {
      if (id.startsWith(prefix)) for (const listener of listeners) listener();
    }
  }

  private notify() {
    for (const listener of this.listeners) listener();
  }
}
